import { Box, Button, Paper, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import { fontMono } from "src/design/typography";
import {
  AwardChip,
  DataTable,
  HeroBand,
  InlineAlert,
  JoinCodeCard,
  LicensePausedNotice,
  Loader,
  LoadingButton,
  ScoreCell,
  StationCard,
  StatusChip,
  useLicenseSuspended,
} from "src/grading/components";
import type { Award } from "src/grading/components/AwardChip/AwardChip";
import { formatScore } from "src/grading/components/EditScoreDrawer/EditScoreDrawer";
import type { ScoreTone } from "src/grading/components/ScoreCell/ScoreCell";
import StudentCell from "src/grading/components/StudentCell/StudentCell";
import TallyRow from "src/grading/components/TallyRow/TallyRow";
import { modificationLabel } from "src/grading/utils/accommodationLabels";
import { useSessionSocket } from "src/hooks/useSessionSocket";
import type { ApiError } from "src/models";
import {
  Category,
  CategoryResult,
  EVENT_LABEL,
  EventId,
  SessionAction,
  StudentResult,
} from "src/models/sessions";
import { useAppDispatch } from "src/store/hooks";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import {
  useGetReviewQuery,
  useGetSessionQuery,
  useTransitionSessionMutation,
  useUpdateStationMutation,
} from "src/store/reducers/sessions/sessionsApiSlice";
import EventIcon from "./planner/EventIcon";

const CATEGORIES: Category[] = ["core", "cardio", "upper"];
const PREVIEW_ROWS = 13;
const FEED_ROWS = 6;
/** The "Just in" summary is announced at most this often, so arriving scores do not chatter. */
const ANNOUNCE_MS = 5_000;

/** The design's white pill on the hero surface. */
const heroButtonSx = {
  bgcolor: "background.paper",
  color: "text.primary",
  fontSize: 15,
  fontWeight: 800,
  minHeight: 50,
  px: 3,
  alignSelf: { md: "flex-end" },
  "&:hover": { bgcolor: "background.paper", transform: "translateY(-1px)" },
} as const;

const messageOf = (e: unknown, fallback: string) => {
  const message = (e as ApiError | undefined)?.data?.message;
  return Array.isArray(message) ? message.join(", ") : (message ?? fallback);
};

/** "Tue Oct 6" from a "YYYY-MM-DD" date, read as a calendar date (not UTC). */
const shortDate = (value: string, locale: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return date.toLocaleDateString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

/** Tone of one category score: tier met, excluded (adaptation or alternative), modified, or waiting. */
const toneOf = (r: CategoryResult | undefined): ScoreTone => {
  if (!r) return "w";
  if (
    r.excluded ||
    r.modification === "adaptation" ||
    r.modification === "alternative"
  )
    return "x";
  if (r.modification === "modification") return "m";
  if (r.presidentialMet) return "p";
  if (r.nationalMet) return "n";
  return "b";
};

const awardOf = (s: StudentResult): Award =>
  s.status === "absent" || s.status === "excused" ? s.status : s.awardTier;

/** Session overview: status, actions, join code, stations, award outlook, a grid preview and the live feed. */
const SessionDetailPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data: session, isLoading, refetch } = useGetSessionQuery(sessionId);
  // FR-043: a suspended license pauses new sessions and score entry.
  const licenseSuspended = useLicenseSuspended();
  const {
    data: review,
    isLoading: reviewLoading,
    refetch: refetchReview,
  } = useGetReviewQuery(sessionId, { skip: !sessionId });
  const [transition, { isLoading: busy }] = useTransitionSessionMutation();
  const [updateStation] = useUpdateStationMutation();
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(() => {
    void refetch();
    void refetchReview();
  }, [refetch, refetchReview]);
  const { connected } = useSessionSocket(sessionId, refresh, refresh);
  const name = session?.name;

  useEffect(() => {
    if (!name) return undefined;
    document.title = `${name} · ${t("app.name")}`;
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({ label: t("tabs.sessions"), path: "/grading/sessions" }),
    );
    dispatch(addBreadcrumb({ label: name }));
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, name, t]);

  // Screen readers hear a count of new scores, not every arriving row.
  const liveCount = session
    ? session.entries.filter((e) => !e.supersededBy).length
    : undefined;
  const [announcement, setAnnouncement] = useState("");
  const announcedCount = useRef<number | undefined>(undefined);
  const lastAnnouncedAt = useRef(0);
  useEffect(() => {
    if (liveCount === undefined) return undefined;
    if (announcedCount.current === undefined) {
      announcedCount.current = liveCount;
      return undefined;
    }
    const fresh = liveCount - announcedCount.current;
    if (fresh <= 0) return undefined;
    const wait = Math.max(
      0,
      lastAnnouncedAt.current + ANNOUNCE_MS - Date.now(),
    );
    const timer = window.setTimeout(() => {
      announcedCount.current = liveCount;
      lastAnnouncedAt.current = Date.now();
      setAnnouncement(
        t("sessions.newScores", {
          defaultValue: "{{count}} new scores",
          count: fresh,
        }),
      );
    }, wait);
    return () => window.clearTimeout(timer);
  }, [liveCount, t]);

  const feed = useMemo(() => {
    if (!session) return [];
    const at = (e: { receivedAt?: string; recordedAt: string }) =>
      e.receivedAt ?? e.recordedAt;
    return session.entries
      .filter((e) => !e.supersededBy)
      .sort((a, b) => at(b).localeCompare(at(a)))
      .slice(0, FEED_ROWS)
      .map((e) => {
        const r = session.rosterSnapshot.find(
          (s) => s.studentId === e.studentId,
        );
        return {
          id: e.id,
          time: new Date(at(e)).toLocaleTimeString(i18n.language, {
            hour: "numeric",
            minute: "2-digit",
          }),
          who: r ? `${r.firstName} ${r.lastInitial || r.lastName}.` : "",
          what: `${t(`events.${e.eventId}`, {
            defaultValue: EVENT_LABEL[e.eventId as EventId] ?? e.eventId,
          })} ${formatScore(String(e.eventId), Number(e.value))}`,
        };
      });
  }, [session, i18n.language, t]);

  if (isLoading || !session) return <Loader />;

  const act = async (action: SessionAction) => {
    setError(null);
    try {
      await transition({ id: sessionId, action }).unwrap();
    } catch (err) {
      setError(
        messageOf(
          err,
          t("sessions.transitionError", {
            defaultValue: "Could not change the session status.",
          }),
        ),
      );
    }
  };
  const toggleLock = async (stationId: string, locked: boolean) => {
    setError(null);
    try {
      await updateStation({ sessionId, stationId, body: { locked } }).unwrap();
    } catch (err) {
      setError(
        messageOf(
          err,
          t("sessions.lockError", {
            defaultValue: "Could not change the station lock. Try again.",
          }),
        ),
      );
    }
  };
  const go = (path: string) =>
    history.push(`/grading/sessions/${sessionId}${path}`);
  const activeEntries = session.entries.filter((e) => !e.supersededBy);
  const status = session.status;
  const finalized = status === "finalized";
  const students = review?.students ?? [];
  const summary = review?.summary;
  const openFlags = review
    ? review.flags.filter((f) => f.status === "open").length
    : session.openFlagCount;
  const safety = session.rosterSnapshot.filter(
    (s) => s.plannedModification !== "none",
  );
  const missingData = session.rosterSnapshot.filter(
    (s) => s.ageAtTest === null || !s.benchmarkSex,
  );
  const meta = [
    shortDate(session.date, i18n.language),
    t("sessions.studentCount", {
      defaultValue: "{{count}} students",
      count: session.rosterSnapshot.length,
    }),
    t("sessions.benchmarkVersion", { version: session.benchmarkVersion ?? 1 }),
  ].join(" · ");

  const actions = (() => {
    switch (status) {
      case "planned":
        return null;
      case "open":
        return (
          <>
            <LoadingButton
              variant="outlined"
              color="secondary"
              label={t("sessions.closeStations")}
              loading={busy}
              onClick={() => act("close_stations")}
            />
            <Button variant="contained" onClick={() => go("/review")}>
              {t("sessions.reviewScores", { defaultValue: "Review scores →" })}
            </Button>
          </>
        );
      case "review":
        return (
          <>
            <LoadingButton
              variant="outlined"
              color="secondary"
              label={t("sessions.reopenStations")}
              loading={busy}
              onClick={() => act("reopen_stations")}
            />
            <Button variant="contained" onClick={() => go("/review")}>
              {t("sessions.reviewAndFinalize", {
                defaultValue: "Review & finalize →",
              })}
            </Button>
          </>
        );
      default:
        return (
          <>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => go("/review")}
            >
              {t("sessions.viewFinalGrid", { defaultValue: "View final grid" })}
            </Button>
            <Button variant="contained" onClick={() => go("/certificates")}>
              {t("sessions.certificatesAndLetters", {
                defaultValue: "Certificates & letters →",
              })}
            </Button>
          </>
        );
    }
  })();

  const cardTitle = (text: string, level: "h2" | "h3" = "h3") => (
    <Typography variant={level === "h2" ? "h5" : "h6"} component={level}>
      {text}
    </Typography>
  );
  const windowLabel = t(`sessions.window.${session.window}`);
  const canManageCode = status === "open" || status === "review";
  const stationsOnline = connected
    ? t("sessions.stationsOnline", {
        count: session.stations.length,
        defaultValue: "{{count}} stations online",
      })
    : t("sessions.offlinePolling", {
        defaultValue: "Offline, refreshing every 10 s",
      });

  const legend = (
    <Box
      aria-hidden
      sx={{
        display: "flex",
        gap: 1.75,
        flexWrap: "wrap",
        fontSize: 12,
        fontWeight: 600,
        color: "text.secondary",
      }}
    >
      {(
        [
          ["tierPresidential", t("review.preview.presidential")],
          ["tierNational", t("review.preview.national")],
          ["surfaceAlt", t("common.notYet")],
        ] as const
      ).map(([tone, label]) => (
        <Box
          key={tone}
          component="span"
          sx={{ display: "flex", gap: 0.75, alignItems: "center" }}
        >
          <Box
            component="span"
            sx={(theme) => ({
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: theme.palette[tone],
              boxShadow:
                tone === "surfaceAlt"
                  ? `inset 0 0 0 1px ${theme.palette.divider}`
                  : "none",
            })}
          />
          {label}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
      <HeroBand
        titleSize={44}
        eyebrow={
          <>
            <StatusChip status={status} />
            {status === "open" ? (
              <StatusChip status={connected ? "synced" : "pending"} />
            ) : null}
            <span>{meta}</span>
          </>
        }
        title={
          <>
            {session.name}
            <br />
            <Box component="span" sx={{ display: "block" }}>
              {t("sessions.testDayEyebrow", {
                defaultValue: "{{window}} Test Day",
                window: windowLabel,
              })}
            </Box>
          </>
        }
        aside={
          status === "planned" ? (
            <LoadingButton
              variant="contained"
              label={t("sessions.openAndShowCode", {
                defaultValue: "Open session & show join code",
              })}
              loading={busy}
              onClick={() => act("open")}
              sx={heroButtonSx}
            />
          ) : canManageCode ? (
            <JoinCodeCard
              variant="hero"
              sessionId={sessionId}
              code={session.joinCode}
              canManage
            />
          ) : undefined
        }
      />

      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          justifyContent: "flex-end",
          flexWrap: "wrap",
          mt: -0.75,
        }}
      >
        {actions}
      </Box>

      {licenseSuspended ? <LicensePausedNotice /> : null}
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {missingData.length > 0 ? (
        <InlineAlert
          severity="warning"
          title={t("sessions.missingDataTitle", {
            defaultValue: "Missing age or benchmark table",
          })}
        >
          {missingData.map((s) => `${s.firstName} ${s.lastName}`).join(", ")}
        </InlineAlert>
      ) : null}

      <Box
        component="section"
        aria-label={t("sessions.stations")}
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr)) minmax(0, 1.1fr)",
          },
          alignItems: "stretch",
        }}
      >
        {session.stations.map((s) => {
          const eventName = t(`events.${s.eventId}`, {
            defaultValue: EVENT_LABEL[s.eventId as EventId] ?? s.eventId,
          });
          return (
            <StationCard
              key={s.id}
              category={t(`categories.${s.category}`)}
              eventName={eventName}
              icon={
                <EventIcon
                  eventId={
                    (s.eventId in EVENT_LABEL
                      ? s.eventId
                      : "pft-situps") as EventId
                  }
                  size={26}
                />
              }
              done={activeEntries.filter((e) => e.eventId === s.eventId).length}
              total={
                Object.keys(s.groupAssignments ?? {}).length ||
                session.rosterSnapshot.length
              }
              statusLabel={
                s.locked
                  ? t("sessions.locked", { defaultValue: "Locked" })
                  : s.label
              }
              lockAction={
                finalized
                  ? undefined
                  : {
                      label: s.locked
                        ? t("sessions.unlock", { defaultValue: "Unlock" })
                        : t("sessions.lock", { defaultValue: "Lock" }),
                      ariaLabel: s.locked
                        ? t("sessions.unlockStation", {
                            defaultValue: "Unlock {{station}}",
                            station: s.label,
                          })
                        : t("sessions.lockStation", {
                            defaultValue: "Lock {{station}}",
                            station: s.label,
                          }),
                      onClick: () => void toggleLock(s.id, !s.locked),
                    }
              }
            />
          );
        })}
        <Box
          component="section"
          aria-label={t("sessions.awardOutlook", {
            defaultValue: "Award outlook · preview",
          })}
          sx={{ gridColumn: { sm: "1 / -1", lg: "auto" } }}
        >
          {summary ? (
            <Box
              sx={{
                height: "100%",
                "& > div": { height: "100%", boxSizing: "border-box" },
              }}
            >
              <Box
                sx={(theme) => ({
                  "& [data-testid='tally-row'] > span": {
                    color: theme.palette.tokens.heroMuted,
                  },
                })}
              >
                <TallyRow
                  variant="bars"
                  total={session.rosterSnapshot.length}
                  counts={{
                    presidential: summary.presidential,
                    national: summary.national,
                    participation: summary.participation,
                    incomplete: summary.incomplete,
                    pending: students.filter(
                      (s) => s.status === "makeup_pending",
                    ).length,
                  }}
                  locked={!finalized}
                />
              </Box>
            </Box>
          ) : (
            <Paper sx={{ p: 2.5, height: "100%" }}>
              {cardTitle(
                t("sessions.awardOutlook", {
                  defaultValue: "Award outlook · preview",
                }),
              )}
              {reviewLoading ? (
                <Loader />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("sessions.outlookUnavailable", {
                    defaultValue: "Award outlook is not available yet.",
                  })}
                </Typography>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1fr) 300px",
          },
          alignItems: "start",
        }}
      >
        <Paper component="section" sx={{ overflow: "hidden", minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              px: 2.5,
              py: 2,
            }}
          >
            {cardTitle(
              t("sessions.liveGrid", { defaultValue: "Live class grid" }),
              "h2",
            )}
            {legend}
          </Box>
          <DataTable<StudentResult>
            aria-label={t("console.classGrid")}
            dense
            minWidth={560}
            loading={reviewLoading}
            rows={students.slice(0, PREVIEW_ROWS)}
            getRowKey={(s) => s.studentId}
            emptyState={
              <Typography color="text.secondary" sx={{ p: 2.5 }}>
                {t("sessions.noScoresYet", {
                  defaultValue: "No scores yet.",
                })}
              </Typography>
            }
            columns={[
              {
                key: "student",
                header: t("review.student"),
                render: (s) => (
                  <StudentCell
                    firstName={s.firstName}
                    lastName={s.lastName}
                    meta={[
                      s.benchmarkSex ?? "—",
                      t("review.ageLabel", {
                        defaultValue: "age {{age}}",
                        age: s.ageAtTest ?? "—",
                      }),
                    ].join(" · ")}
                  />
                ),
              },
              ...CATEGORIES.map((c) => ({
                key: c,
                header: t(`categories.${c}`),
                render: (s: StudentResult) => {
                  const r = s.categories[c];
                  return (
                    <ScoreCell
                      tone={toneOf(r)}
                      studentName={`${s.firstName} ${s.lastName}`}
                      value={
                        r
                          ? `${formatScore(r.eventId, r.value)}${r.mode ? ` ${r.mode}` : ""}`
                          : "—"
                      }
                      eventShort={
                        r
                          ? t(`events.${r.eventId}`, {
                              defaultValue:
                                EVENT_LABEL[r.eventId as EventId] ?? r.eventId,
                            })
                          : t(`categories.${c}`)
                      }
                    />
                  );
                },
              })),
              {
                key: "award",
                header: t("review.award"),
                render: (s) => <AwardChip award={awardOf(s)} size="sm" />,
              },
            ]}
          />
          <Typography
            sx={(theme) => ({
              px: 2.5,
              py: 1.75,
              borderTop: `1px solid ${theme.palette.divider}`,
              fontSize: 13,
              fontWeight: 600,
              color: "text.secondary",
            })}
          >
            {t("sessions.showingRows", {
              defaultValue: "Showing {{shown}} of {{total}}",
              shown: Math.min(PREVIEW_ROWS, students.length),
              total: students.length,
            })}
            {" · "}
            <Button
              variant="text"
              size="small"
              onClick={() => go("/review")}
              sx={{
                p: 0,
                minHeight: 0,
                fontSize: 13,
                verticalAlign: "baseline",
              }}
            >
              {t("sessions.openFullGrid", {
                defaultValue: "Open the full grid in Review →",
              })}
            </Button>
          </Typography>
        </Paper>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper
            component="section"
            sx={{
              p: "18px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {cardTitle(t("sessions.justIn", { defaultValue: "Just in" }))}
              <Box
                component="span"
                sx={(theme) => ({
                  display: "flex",
                  gap: 0.75,
                  alignItems: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  color: connected
                    ? theme.palette.successInk
                    : theme.palette.text.secondary,
                })}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: "currentColor",
                  }}
                />
                {stationsOnline}
              </Box>
            </Box>
            <Box role="status" aria-live="polite" sx={visuallyHidden}>
              {announcement}
            </Box>
            {feed.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("sessions.noScoresYet", {
                  defaultValue: "No scores yet.",
                })}
              </Typography>
            ) : (
              <Box
                component="ul"
                aria-live="off"
                sx={{
                  listStyle: "none",
                  m: 0,
                  p: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                {feed.map((f) => (
                  <Box
                    component="li"
                    key={f.id}
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      alignItems: "center",
                      fontSize: 13,
                      lineHeight: 1.3,
                    }}
                  >
                    <Box
                      component="span"
                      aria-hidden
                      sx={{
                        width: 8,
                        height: 8,
                        flex: "none",
                        borderRadius: "50%",
                        bgcolor: "error.main",
                      }}
                    />
                    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                      <b>{f.who}</b> {f.what}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontFamily: fontMono,
                        fontSize: 11,
                        fontWeight: 600,
                        color: "text.disabled",
                        flexShrink: 0,
                      }}
                    >
                      {f.time}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <Box
            component="section"
            aria-labelledby="session-flags"
            sx={(theme) => ({
              p: "18px 20px",
              borderRadius: `${radius.card}px`,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              bgcolor:
                openFlags > 0
                  ? theme.palette.tokens.warningBg
                  : theme.palette.background.paper,
              border: `1.5px solid ${
                openFlags > 0
                  ? theme.palette.warningLine
                  : theme.palette.divider
              }`,
            })}
          >
            <Typography
              id="session-flags"
              variant="h6"
              component="h3"
              sx={{ color: openFlags > 0 ? "warning.dark" : "text.primary" }}
            >
              {openFlags > 0
                ? t("sessions.flagsToSort", {
                    defaultValue: "{{count}} to sort out",
                    count: openFlags,
                  })
                : t("review.noOpenFlags")}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => go("/review")}
              sx={{ alignSelf: "flex-start" }}
            >
              {t("sessions.fixInReview", { defaultValue: "Fix in Review" })}
            </Button>
          </Box>

          <Paper
            component="section"
            sx={{
              p: "18px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {cardTitle(
              t("sessions.safetyAtStations", {
                defaultValue: "Safety at stations",
              }),
            )}
            {safety.length === 0 ? (
              <Typography
                sx={{ fontSize: 13, lineHeight: 1.45, color: "text.secondary" }}
              >
                {t("sessions.noFlags")}
              </Typography>
            ) : (
              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {safety.map((s) => (
                  <Typography
                    component="li"
                    key={s.studentId}
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.45,
                      color: "text.secondary",
                    }}
                  >
                    {`${s.firstName} ${s.lastName} · ${modificationLabel(t, s.plannedModification)}${s.plannedNote ? ` · ${s.plannedNote}` : ""}`}
                  </Typography>
                ))}
              </Box>
            )}
            <Typography
              component="p"
              sx={{ fontSize: 12, lineHeight: 1.4, color: "text.secondary" }}
            >
              {t("classes.accommodationAccess", {
                defaultValue:
                  "Visible to staff with accommodation access. Volunteer scorers never see health notes.",
              })}
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default SessionDetailPage;
